import React, { useState, useRef, useEffect } from "react";
import { X, ThumbsUp, Trash2, Check, MessageSquare, Send, ChevronDown, ChevronUp } from "lucide-react";
import useCanvasCommentsStore from "../store/useCanvasCommentsStore";
import useGlobalStore from "@/store/globalStore";

interface CanvasCommentsDrawerProps {
  versionId: string;
  /** The resolved interface UUID for the currently active device — comes from canvas data, never hardcoded */
  interfaceId: string;
  pendingPin: { x: number; y: number } | null;
  onClose: () => void;
  currentUserId?: string;
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  const safeName = name || "?";
  const initials = safeName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const colors = ["#EA33C0", "#F5A623", "#4A90E2", "#7ED321", "#9B59B6", "#E67E22"];
  const color = colors[safeName.charCodeAt(0) % colors.length];
  return (
    <div
      style={{ width: size, height: size, backgroundColor: color, fontSize: size * 0.38, flexShrink: 0 }}
      className="rounded-full flex items-center justify-center text-white font-semibold select-none"
    >
      {initials || "?"}
    </div>
  );
}

export const CanvasCommentsDrawer: React.FC<CanvasCommentsDrawerProps> = ({
  versionId,
  interfaceId,
  pendingPin,
  onClose,
  currentUserId,
}) => {
  const {
    comments,
    activeCommentId,
    isSaving,
    addComment,
    addReply,
    toggleLike,
    toggleResolve,
    deleteComment,
    closeDrawer,
  } = useCanvasCommentsStore();
  const { showSuccess, showError } = useGlobalStore();

  const [newCommentText, setNewCommentText] = useState("");
  const [replyText, setReplyText] = useState("");
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const replyRef = useRef<HTMLTextAreaElement>(null);

  const activeComment = activeCommentId
    ? comments.find((c) => c.id === activeCommentId) ?? null
    : null;

  const isNewMode = !activeCommentId && !!pendingPin;

  useEffect(() => {
    if (isNewMode && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isNewMode]);

  useEffect(() => {
    if (showReplyInput && replyRef.current) {
      setTimeout(() => replyRef.current?.focus(), 100);
    }
  }, [showReplyInput]);

  const handleSubmitNew = async () => {
    if (!newCommentText.trim() || !pendingPin) return;
    try {
      await addComment(versionId, {
        comment: newCommentText.trim(),
        x_position: pendingPin.x,
        y_position: pendingPin.y,
        interface_id: interfaceId,
      });
      setNewCommentText("");
      showSuccess("Comment added.");
      closeDrawer();
    } catch {
      showError("Failed to add comment.");
    }
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim() || !activeCommentId) return;
    try {
      await addReply(versionId, activeCommentId, replyText.trim());
      setReplyText("");
      setShowReplyInput(false);
      showSuccess("Reply added.");
    } catch {
      showError("Failed to add reply.");
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await deleteComment(commentId);
      showSuccess("Comment deleted.");
      if (commentId === activeCommentId) {
        closeDrawer();
      }
    } catch {
      showError("Failed to delete comment.");
    }
  };

  const handleClose = () => {
    closeDrawer();
    onClose();
  };

  return (
    <div className="fixed inset-y-0 right-0 z-50 flex">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20" onClick={handleClose} />

      {/* Drawer */}
      <div
        className="relative ml-auto bg-white shadow-2xl flex flex-col"
        style={{ width: 400, height: "100vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-white flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <MessageSquare size={18} className="text-pink-500" />
            <span className="font-semibold text-gray-900 text-base">
              {isNewMode ? "Add Comment" : "Comment"}
            </span>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
          >
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {isNewMode ? (
            /* ── New Comment Form ── */
            <div className="p-5">
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                  style={{ backgroundColor: "#EA33C0" }}
                />
                <div className="text-xs text-gray-400">
                  Drop a comment at ({Math.round(pendingPin!.x)}, {Math.round(pendingPin!.y)})
                </div>
              </div>
              <textarea
                ref={textareaRef}
                value={newCommentText}
                onChange={(e) => setNewCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSubmitNew();
                  }
                }}
                placeholder="Add a comment… (⌘+Enter to submit)"
                rows={4}
                maxLength={2000}
                className="w-full px-3.5 py-3 text-sm border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent placeholder-gray-400 text-gray-800 leading-relaxed"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-400">{newCommentText.length}/2000</span>
                <div className="flex gap-2">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-600 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitNew}
                    disabled={!newCommentText.trim() || isSaving}
                    className="px-4 py-2 text-sm font-medium text-white rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ backgroundColor: newCommentText.trim() && !isSaving ? "#EA33C0" : undefined, background: !newCommentText.trim() || isSaving ? "#d1d5db" : "#EA33C0" }}
                  >
                    {isSaving ? "Posting…" : "Post"}
                  </button>
                </div>
              </div>
            </div>
          ) : activeComment ? (
            /* ── View Comment Thread ── */
            <div className="p-5 space-y-5">
              {/* Resolved badge */}
              {activeComment.is_resolved && (
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg border border-green-100">
                  <Check size={14} className="text-green-600" />
                  <span className="text-xs font-medium text-green-700">
                    Resolved{activeComment.resolved_by ? ` by ${activeComment.resolved_by.name}` : ""}
                  </span>
                </div>
              )}

              {/* Main comment */}
              <div className="group">
                <div className="flex items-start gap-3">
                  <Avatar name={activeComment.author.name} size={34} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-900">{activeComment.author.name}</span>
                      <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(activeComment.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed break-words">{activeComment.comment}</p>

                    {/* Actions */}
                    <div className="flex items-center gap-3 mt-3">
                      <button
                        onClick={() => toggleLike(activeComment.id)}
                        className={`flex items-center gap-1.5 text-xs font-medium transition-colors px-2.5 py-1.5 rounded-full ${
                          activeComment.liked_by_me
                            ? "bg-pink-50 text-pink-600"
                            : "bg-gray-50 text-gray-500 hover:bg-pink-50 hover:text-pink-500"
                        }`}
                      >
                        <ThumbsUp size={12} className={activeComment.liked_by_me ? "fill-pink-500 text-pink-500" : ""} />
                        <span>{activeComment.likes_count > 0 ? activeComment.likes_count : "Like"}</span>
                      </button>

                      <button
                        onClick={() => setShowReplyInput((v) => !v)}
                        className="flex items-center gap-1.5 text-xs font-medium bg-gray-50 text-gray-500 hover:bg-blue-50 hover:text-blue-500 transition-colors px-2.5 py-1.5 rounded-full"
                      >
                        <MessageSquare size={12} />
                        <span>Reply</span>
                      </button>

                      <button
                        onClick={() => toggleResolve(activeComment.id)}
                        className={`flex items-center gap-1.5 text-xs font-medium transition-colors px-2.5 py-1.5 rounded-full ${
                          activeComment.is_resolved
                            ? "bg-green-50 text-green-600 hover:bg-gray-50 hover:text-gray-500"
                            : "bg-gray-50 text-gray-500 hover:bg-green-50 hover:text-green-600"
                        }`}
                      >
                        <Check size={12} />
                        <span>{activeComment.is_resolved ? "Unresolve" : "Resolve"}</span>
                      </button>

                      {(currentUserId === activeComment.author.id || !currentUserId) && (
                        <button
                          onClick={() => handleDelete(activeComment.id)}
                          className="flex items-center gap-1.5 text-xs font-medium bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors px-2.5 py-1.5 rounded-full ml-auto"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider + replies toggle */}
              {activeComment.replies_count > 0 && (
                <button
                  onClick={() => setExpandedReplies((v) => !v)}
                  className="flex items-center gap-2 w-full text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors py-1"
                >
                  <div className="flex-1 h-px bg-gray-100" />
                  <span>{expandedReplies ? "Hide" : "Show"} {activeComment.replies_count} {activeComment.replies_count === 1 ? "reply" : "replies"}</span>
                  {expandedReplies ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  <div className="flex-1 h-px bg-gray-100" />
                </button>
              )}

              {/* Replies */}
              {expandedReplies && activeComment.replies.map((reply) => (
                <div key={reply.id} className="pl-4 border-l-2 border-gray-100 group">
                  <div className="flex items-start gap-3">
                    <Avatar name={reply.author.name} size={28} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-sm font-semibold text-gray-900">{reply.author.name}</span>
                        <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(reply.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed break-words">{reply.comment}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <button
                          onClick={() => toggleLike(reply.id)}
                          className={`flex items-center gap-1.5 text-xs font-medium transition-colors px-2.5 py-1.5 rounded-full ${
                            reply.liked_by_me
                              ? "bg-pink-50 text-pink-600"
                              : "bg-gray-50 text-gray-500 hover:bg-pink-50 hover:text-pink-500"
                          }`}
                        >
                          <ThumbsUp size={12} className={reply.liked_by_me ? "fill-pink-500 text-pink-500" : ""} />
                          <span>{reply.likes_count > 0 ? reply.likes_count : "Like"}</span>
                        </button>
                        {(currentUserId === reply.author.id || !currentUserId) && (
                          <button
                            onClick={() => handleDelete(reply.id)}
                            className="flex items-center gap-1.5 text-xs font-medium bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors px-2.5 py-1.5 rounded-full ml-auto"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Reply input */}
              {showReplyInput && (
                <div className="pt-2">
                  <div className="flex items-end gap-2">
                    <textarea
                      ref={replyRef}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          handleSubmitReply();
                        }
                      }}
                      placeholder="Write a reply… (⌘+Enter)"
                      rows={2}
                      maxLength={2000}
                      className="flex-1 px-3.5 py-3 text-sm border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent placeholder-gray-400 text-gray-800 leading-relaxed"
                    />
                    <button
                      onClick={handleSubmitReply}
                      disabled={!replyText.trim() || isSaving}
                      className="w-9 h-9 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 flex-shrink-0"
                      style={{ backgroundColor: replyText.trim() && !isSaving ? "#EA33C0" : "#d1d5db" }}
                    >
                      <Send size={14} className="text-white" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-40 text-gray-400">
              <MessageSquare size={24} className="mb-2 opacity-40" />
              <span className="text-sm">No comment selected</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
